import React, { FC } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

import { FeatureGridBlockFragmentFragment, FeatureGridItemFragmentFragment } from "@graphqlTypes"

import {
  convertStringToSlug,
  generateConditionalClassName,
  processShortcodes,
} from "@utils"

import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import LpSection from "../LpSection/LpSection"

import "./FeatureGridBlock.scss"
import AnchorLink from "@components/AnchorLink/AnchorLink"
import WistiaPopover from "@components/WistiaPopover/WistiaPopover"

const FeatureGridBlockItem: FC<FeatureGridItemFragmentFragment & {
  hasTitleOrImage: boolean,
  use2UpGrid?: boolean | null,
}> = ({
  hasTitleOrImage,
  icon,
  image,
  text,
  title,
  url,
  use2UpGrid,
  wistiaId,
}) => {
    const className = generateConditionalClassName({
      "FeatureGridBlock--Item": true,
      "flex-item--1": true,
      "flex-item--2-S": true,
      // 2-UP GRID
      "flex-item--2-M": hasTitleOrImage || !!use2UpGrid,
      // 3-UP GRID
      "flex-item--3-M": !hasTitleOrImage && !use2UpGrid,
      "is-link": !!url,
    })

    const _url = url && processShortcodes(url)

    const _children = (
      <>
        {image?.imgixImage?.gatsbyImageData && (wistiaId ? (
          <WistiaPopover
            imageAlt={image.title || title || "Image"}
            imageData={image.imgixImage.gatsbyImageData}
            id={wistiaId}
          />
        ) : (
          <GatsbyImage
            alt={image.title || title || "Image"}
            image={image.imgixImage.gatsbyImageData}
          />
        ))}
        <h6>
          {icon && Object.keys(ICON_TYPES).includes(icon) && (
            <Icon size="calc(var(--font-size--L) * var(--line-height--tight))" type={icon as ICON_TYPES} />
          )}
          {title}
        </h6>
        {text && <p>{text}</p>}
      </>
    )

    if (!_url) return <div className={className}>{_children}</div>
    return <AnchorLink className={className} href={_url}>{_children}</AnchorLink>
  }

const FeatureGridBlock: FC<FeatureGridBlockFragmentFragment> = ({
  background,
  contentful_id,
  gridItems,
  image,
  includeBackground,
  marginBottom,
  title,
  use2UpGrid,
  useDarkMode,
}) => {
  if (!gridItems) return null

  const hasTitleOrImage = !!title || !!image?.imgixImage?.gatsbyImageData

  return (
    <LpSection
      background={background}
      className="FeatureGridBlock"
      includeBackground={includeBackground}
      marginBottom={marginBottom}
      uniqueId={contentful_id}
      useDarkMode={useDarkMode}
    >
      <div className="container">
        <div className="grid">
          {hasTitleOrImage && (
            <div className="headline flex column column--1-5-M">
              {title && <h2 id={convertStringToSlug(title)}>{title}</h2>}
              {image && (
                <GatsbyImage
                  alt={image.title || title || "Image"}
                  image={image.imgixImage?.gatsbyImageData}
                />
              )}
            </div>
          )}
          <div className={generateConditionalClassName({
            [`items flex column`]: true,
            "column--5-13-M": hasTitleOrImage,
          })}>
            {gridItems.map(item => !item ? null : (
              <FeatureGridBlockItem
                key={item.title}
                {...item}
                hasTitleOrImage={hasTitleOrImage}
                use2UpGrid={use2UpGrid}
              />
            ))}
          </div>
        </div>
      </div>
    </LpSection>
  )
}

export const FeatureGridBlockFragment = graphql`
  fragment FeatureGridItemFragment on ContentfulLpFeatureGridItem {
    id
    icon
    image {
      title
      imgixImage {
        gatsbyImageData(width: 770)
      }
    }
    text
    title
    url
    wistiaId
  }

  fragment FeatureGridBlockFragment on ContentfulLpFeatureGrid {
    contentful_id
    image {
      title
      imgixImage {
        gatsbyImageData(width: 434)
      }
    }
    gridItems: items {
      ...FeatureGridItemFragment
    }
    title
    use2UpGrid
    background
    includeBackground
    marginBottom
    useDarkMode
  }
`

export default FeatureGridBlock
